/* eslint-disable no-restricted-syntax */
import { ReactNode } from 'react'
import { memoize } from 'lodash'

import { PermissionChoices } from 'src/gen-types'
import { impersonatorIsStaffVar } from 'src/api/impersonator-me'
import { impersonatorTokenKey } from 'src/utils/impersonator'

import { RouteConfig, Roles } from './child-router-factory'
import { routingMap } from './routing-map'

export type Role =
  | `authenticated`
  | `non-authenticated`
  | `wastehero-staff`
  // Company master (user.isMaster). Derived in setRoles from the `me` payload,
  // same mechanism as `wastehero-staff`. Mirrors the backend
  // core.permissions.user_can_manage_company_feature_flags gate (#1433), which
  // accepts staff OR master — so FE surfaces gated on staff-or-master (the
  // company-settings Features tab) agree with server acceptance. NOT Django
  // staff: a master is a company-level admin persona.
  | `master`
  | `live-operations`
  | `hauler`
  // Per-company Hauler-module opt-in (companySettings.haulerModuleEnabled).
  // Granted in setRoles from the `me` payload — same mechanism as the
  // per-company `live-operations` role. Missing/undefined field → NOT granted
  // (fail-safe OFF), so customers on an older backend never see the module.
  | `hauler-module`
  // Per-company Invoicing opt-in (companySettings.invoicingEnabled). Granted
  // in setRoles from the `me` payload — same mechanism as `hauler-module`.
  // Missing/undefined field → NOT granted (fail closed), so a company with
  // invoicing off (e.g. legacy-pricing tenants) can never deep-link into the
  // /app/invoicing module.
  | `invoicing`
  // The two ACCOUNTING registers inside the invoicing module, one tab each
  // (companySettings.invoicingAccountingAccountsEnabled /
  // invoicingRoutingRulesEnabled). Granted in setRoles like `invoicing`
  // itself, and required by those two entries in INVOICING_PAGES — so the tab
  // is filtered out of the strip AND its list/detail/create routes are walled.
  // Default OFF on the backend: "invoicing" is the invoice-producing half
  // only until staff opt a tenant into the accounting half, so a
  // missing/undefined field reads as OFF (fail closed) exactly like the
  // module flag above.
  | `invoicing-accounting-accounts`
  | `invoicing-routing-rules`
  | `live-monitoring`
  // NEXUS-734: customer-registry surfaces (properties/SCP registry data) are
  // none of a pure driver's business — granted to every authenticated user
  // EXCEPT pure drivers (a driver profile with no admin-grade flag).
  | `customer-registry`
  | `impersonator`
  // The real staff user behind an impersonation session is staff. Granted from
  // the impersonator token's `me { isStaff }` (src/api/impersonator-me), NOT
  // from the impersonated `me` — deliberately narrower than widening
  // `wastehero-staff`, which also gates tenant-facing surfaces. Lets the
  // feature-config admin stay reachable while impersonating (PRO-459).
  | `staff-impersonator`
  | `hidden` // this role is used for temporary hidden pages

export type Middleware = (roles: Role[], opt?: string) => string
export type WithRoutingMap = (map: typeof routingMap) => Middleware

export type RoutingMap = typeof routingMap

export type RoutingState = {
  roles: Role[]
  routingMap: typeof routingMap
  init: boolean
}

type ANY = Record<string, unknown>

type ChainWithTitleArgs = {
  title: ReactNode
  breadCrumbTitle: ReactNode
}

export type CreateConfigFn<Keys extends string, Conds extends ANY = ANY> = (
  map: typeof routingMap,
  utils?: {
    conds: Conds
  }
) => RouteConfig<Keys>[]

export type CreateConfigFnExtend<
  T extends ANY,
  Keys extends string,
  Conds extends ANY = ANY
> = (props: T) => CreateConfigFn<Keys, Conds>

export const nonAuthenticatedRedirect: WithRoutingMap =
  (map) =>
  (rolesC, opt = ``) => {
    if (rolesC.indexOf('authenticated') === -1) return map.login._
    return opt
  }

export const authenticatedRedirect: WithRoutingMap =
  (map) =>
  (rolesC, opt = ``) => {
    if (rolesC.indexOf('non-authenticated') === -1) return map.app.dashboard._
    return opt
  }

/**
 * NEXUS-734: customer-registry routes deny the pure-driver persona with an
 * explicit dashboard redirect (RoleChecker renders a <Redirect> — the app's
 * standard unauthorized-route pattern), never an infinite spinner.
 */
export const customerRegistryRedirect: WithRoutingMap =
  (map) =>
  (rolesC, opt = ``) => {
    if (rolesC.indexOf('authenticated') === -1) return map.login._
    if (rolesC.indexOf('customer-registry') === -1) return map.app.dashboard._
    return opt
  }

export const blockHaulerRedirect: WithRoutingMap =
  (map) =>
  (rolesC, opt = ``) => {
    if (rolesC.indexOf('authenticated') === -1) return map.login._
    // The contractor's default landing (roadmap 29): the workspace overview.
    // This is also where haulers land right after login (the post-login
    // `app/dashboard` route is office-only and falls back through here).
    if (rolesC.indexOf('hauler') !== -1) return map.app.contractor.home._
    return opt
  }

// Named for what it protects (structural/admin content), not who it excludes, so it
// stays accurate if another persona ever needs the same gate.
export const structuralOnly: Roles = {
  and: ['authenticated'],
  not: ['hauler'],
}

// Redirects office (non-hauler) users away from the dedicated contractor
// workspace (SPEC §4 W4). Mirror of blockHaulerRedirect: the contractor
// persona screens are hauler-scoped and meaningless for office users, so send
// them back to their dashboard rather than showing an empty/irrelevant view.
export const blockOfficeRedirect: WithRoutingMap =
  (map) =>
  (rolesC, opt = ``) => {
    if (rolesC.indexOf('authenticated') === -1) return map.login._
    if (rolesC.indexOf('hauler') === -1) return map.app.dashboard._
    return opt
  }

// The inverse gate of `structuralOnly`: the contractor (hauler) workspace.
// (`hauler` is only ever granted when the company's Hauler module is enabled —
// see setRoles — so this gate implies the module flag too.)
export const haulerOnly: Roles = {
  and: ['authenticated', 'hauler'],
}

// OFFICE Hauler-module screens (overview, proposals, tasks, unloading,
// settlement, reliability, complaints, contractor/contract-area settings):
// structural content that additionally requires the per-company module flag.
// With the flag off these routes redirect away cleanly (no dead URLs).
export const haulerModuleOnly: Roles = {
  and: ['authenticated', 'hauler-module'],
  not: ['hauler'],
}

// Fallback for `haulerModuleOnly` routes. Order matters: unauthenticated →
// login; contractor users → their workspace; office users without the module
// flag → the dashboard (never an empty-string redirect, which would loop).
export const haulerModuleOffRedirect: WithRoutingMap =
  (map) =>
  (rolesC, opt = ``) => {
    if (rolesC.indexOf('authenticated') === -1) return map.login._
    if (rolesC.indexOf('hauler') !== -1) return map.app.contractor.home._
    if (rolesC.indexOf('hauler-module') === -1) return map.app.dashboard._
    return opt
  }

// The /app/invoicing module (Events, Scheduled billing, Billing runs,
// Simulations, Invoices, Accounting accounts, Routing rules, Credit notes):
// structural content that additionally requires the per-company invoicing
// opt-in. Mirrors `haulerModuleOnly` — with the flag off the routes redirect
// away cleanly (fail closed, no dead URLs).
export const invoicingModuleOnly: Roles = {
  and: ['authenticated', 'invoicing'],
  not: ['hauler'],
}

// Fallback for `invoicingModuleOnly` routes — mirror of
// `haulerModuleOffRedirect`. Order matters: unauthenticated → login;
// contractor users → their workspace; office users without the invoicing
// flag → the dashboard (never an empty-string redirect, which would loop).
export const invoicingModuleOffRedirect: WithRoutingMap =
  (map) =>
  (rolesC, opt = ``) => {
    if (rolesC.indexOf('authenticated') === -1) return map.login._
    if (rolesC.indexOf('hauler') !== -1) return map.app.contractor.home._
    if (rolesC.indexOf('invoicing') === -1) return map.app.dashboard._
    return opt
  }

export function setRoles(user: $TSFixMe): Role[] {
  let givenRoles: Role[] = []
  if (user && user.id) {
    givenRoles = ['authenticated']
    // NEXUS-734: a PURE driver services routes; the customer registry is
    // walled off. Mirrors the BE's User.is_pure_driver (a Driver profile
    // with no back-office flag and no company role — staff who also drive
    // are not pure drivers). Every other authenticated user keeps registry
    // access — including payloads without the isDriver marker (GET_ME ships
    // it; legacy/impersonation payloads may not).
    const isPureDriver =
      Boolean(user.isDriver) &&
      !user.isStaff &&
      !user.isSuperuser &&
      !user.isMaster &&
      !user.isAdmin &&
      !user.isReseller &&
      !user.companyRole
    if (!isPureDriver) {
      givenRoles.push('customer-registry')
    }
    if (user.isStaff) {
      givenRoles.push('wastehero-staff')
    }
    // Company master. A missing/undefined field (older backend) is falsy →
    // NOT granted (fail-safe OFF). Consumed by the company-settings Features
    // tab gate, matching the backend user_can_manage_company_feature_flags
    // (staff-or-master, #1433).
    if (user.isMaster) {
      givenRoles.push('master')
    }
    // Live Operations access (SPEC D4). The grant is the *real*
    // server-enforced permission gated behind the per-tenant feature flag —
    // no hardcoded company id, no staff-only shortcut. This mirrors the
    // backend helper core.permissions.user_can_access_live_operations and the
    // useUserRolePermissions().has() bypass exactly, so nav/route visibility
    // and server acceptance agree by construction. Under impersonation `user`
    // is the impersonated user's `me` (token swapped + page reloaded), so the
    // impersonated tenant's flag + role are evaluated automatically.
    const liveOperationsEnabled =
      user?.company?.companySettings?.liveOperationsEnabled === true
    const canAccessLiveOperations =
      Boolean(user.isSuperuser) ||
      Boolean(user.isStaff) ||
      Boolean(user.isMaster) ||
      Boolean(
        user?.permissions?.includes(PermissionChoices.LIVE_OPERATIONS_VIEW)
      )
    if (liveOperationsEnabled && canAccessLiveOperations) {
      givenRoles.push('live-operations')
    }
    // Per-company Hauler-module opt-in. Boolean() makes a missing/undefined
    // field (older backend) read as OFF — fail-safe: with the flag off the FE
    // must look exactly as it did before the hauler build.
    const haulerModuleEnabled = Boolean(
      user?.company?.companySettings?.haulerModuleEnabled
    )
    if (haulerModuleEnabled) {
      givenRoles.push('hauler-module')
    }
    // Per-company Invoicing opt-in. Boolean() makes a missing/undefined field
    // (older backend, partial payload) read as OFF — fail closed: with the
    // flag off the /app/invoicing module must be unreachable, deep links
    // included, for EVERYONE (no staff/master bypass — mirrors the
    // live-operations flag's hard outer gate).
    const invoicingEnabled = Boolean(
      user?.company?.companySettings?.invoicingEnabled
    )
    if (invoicingEnabled) {
      givenRoles.push('invoicing')
      // The accounting half of the module, one flag per tab. Nested inside the
      // module grant on purpose (belt + braces with the backend invariant that
      // rejects a tab flag while invoicing is off): a stored tab flag can
      // never surface a tab for a tenant who has no invoicing module.
      if (
        user?.company?.companySettings?.invoicingAccountingAccountsEnabled ===
        true
      ) {
        givenRoles.push('invoicing-accounting-accounts')
      }
      if (
        user?.company?.companySettings?.invoicingRoutingRulesEnabled === true
      ) {
        givenRoles.push('invoicing-routing-rules')
      }
    }
    // `!user.isStaff` wins over isHaulerUser on purpose: WasteHero staff need full
    // visibility for support/impersonation even if their account is ever also
    // hauler-linked (e.g. test data, dual-tagged accounts) — staff must never lose
    // office access because of a hauler flag.
    // Also gated on the company module flag (belt + braces — hauler users
    // shouldn't exist when the module is off; if one does, they fall back to
    // the plain office experience and the server denies hauler-scoped data).
    if (user.isHaulerUser && !user.isStaff && haulerModuleEnabled) {
      givenRoles.push('hauler')
    }
    // if there is root token available then this user is impersonating.
    if (localStorage.getItem(impersonatorTokenKey)) {
      givenRoles.push('impersonator')
    }
    // The real staff behind the impersonation is staff (resolved in the boot
    // batch via the impersonator token — see src/api/impersonator-me). Kept
    // separate from `wastehero-staff` so tenant-facing staff surfaces are not
    // widened; only the explicitly staff-or-impersonator gates admit it.
    if (impersonatorIsStaffVar()) {
      givenRoles.push('staff-impersonator')
    }
  } else givenRoles = ['non-authenticated']

  return givenRoles
}

export const slashJoin = (...args: string[]): string =>
  '/'.concat(args.join('/'))

export const pathJoin = (...args: string[]) => {
  const resolve = (...args1: string[]) => {
    return pathJoin(...args, ...args1)
  }
  resolve.value = '/'.concat(args.filter((v) => !!v).join('/'))
  resolve.chain = { _: resolve.value }
  resolve.chainWithTitle = (chainArgs: ChainWithTitleArgs) => ({
    _: resolve.value,
    __TITLE: chainArgs.title,
    __BREADCRUMBTITLE: chainArgs.breadCrumbTitle,
  })
  resolve.chainMap = <M = ANY>(map: M) => ({ ...resolve.chain, ...map })
  resolve.chainMapAuto = <M = ANY>(
    createMap: (prefix: string) => M,
    chainAutoArgs?: ChainWithTitleArgs
  ) => ({
    ...(chainAutoArgs ? resolve.chainWithTitle(chainAutoArgs) : resolve.chain),
    ...createMap(resolve.value.slice(1)),
  })
  return resolve
}

// eslint-disable-next-line consistent-return
export const getRouteTitle = memoize((route: string): ReactNode | undefined => {
  const mapsArr: $TSFixMe = [routingMap]
  let skipNext = false
  let routeParts = route.split('/')
  routeParts = routeParts.filter((r) => r)

  // eslint-disable-next-line consistent-return
  const findSubNameKey = (map: $TSFixMe, routePart: string) => {
    // for cases like 'edit/:id
    // where we have double function, and the other function is another route.
    for (const key in map) {
      // keys may be slash-prefixed (e.g. '/company/:id'), so compare against
      // the key's leading path segment rather than the raw key.
      if (Object.prototype.hasOwnProperty.call(map, key)) {
        const keySegment = key.replace(/^\//, '').split('/')[0]
        if (keySegment === routePart && typeof map[key] === 'function') {
          return map[key](':id')
        }
      }
    }
  }

  routeParts.forEach((routePart) => {
    const lastMap = mapsArr.at(-1)
    const newMap = lastMap[routePart]

    if (skipNext) {
      skipNext = false
      return
    }

    if (newMap) {
      mapsArr.push(newMap)
    } else if (findSubNameKey(lastMap, routePart)) {
      mapsArr.push(findSubNameKey(lastMap, routePart))
      skipNext = true
    } else if (lastMap[':id'] && typeof lastMap[':id'] === 'function') {
      mapsArr.push(lastMap[':id'](':id'))
    } else {
      // eslint-disable-next-line guard-for-in
      for (const key in lastMap) {
        if (typeof lastMap[key] === 'function') {
          mapsArr.push(lastMap[key](':id'))
          break
        }
      }
    }
  })

  for (const map of mapsArr.reverse()) {
    // eslint-disable-next-line no-underscore-dangle
    if (map.__TITLE) {
      // eslint-disable-next-line no-underscore-dangle
      return map.__TITLE
    }
  }
})
